import axiosInstance from '../auth/axiosInstance';
import { getApiErrorMessage } from '../types/api';
import {
  normalizeApiRoleList,
  type ApiRolePermissions,
} from '../utils/rolePermissionsUtils';

/**
 * Role-based menu permissions API.
 *
 * Despite legacy endpoint names ("User" in the path), all operations are keyed
 * by `roleId`. Permissions apply to every user assigned that role.
 */

const ROLE_PERMISSIONS_ENDPOINT = '/Menu/AllUsersRolesPermissions';
const SAVE_ROLE_PERMISSIONS_ENDPOINT = '/Menu/AddOrUpdateUserMenuPermission';

type SavePermissionDetail = {
  menuId: number;
  permissionId: number;
  isAllowed: 0 | 1;
};

const throwIfFailed = (
  result: { code?: number; message?: string; data?: unknown } | null | undefined,
  fallback: string
) => {
  if (typeof result?.code === 'number' && result.code >= 400) {
    throw new Error(result.message || fallback);
  }
  return result;
};

/**
 * Fetches every role that has menu permissions configured.
 */
export const fetchRolePermissionsList = async (): Promise<ApiRolePermissions[]> => {
  try {
    const { data } = await axiosInstance.get(ROLE_PERMISSIONS_ENDPOINT);
    throwIfFailed(data, 'Failed to load role permissions');
    return normalizeApiRoleList(data?.data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || 'Failed to load role permissions');
  }
};

/**
 * Creates or updates menu permissions for a role, then returns the refreshed list.
 *
 * Only permissions with `isAllowed === 1` are sent — the API treats omitted
 * entries as revoked on save.
 */
export const saveRoleMenuPermissions = async (
  roleId: number,
  permissionDetails: SavePermissionDetail[]
): Promise<ApiRolePermissions[]> => {
  const payload = {
    id: 0,
    roleId,
    addMenuPermissionDetailDto: permissionDetails.filter((detail) => detail.isAllowed === 1),
  };

  try {
    const { data } = await axiosInstance.post(SAVE_ROLE_PERMISSIONS_ENDPOINT, payload);
    throwIfFailed(data, 'Failed to save role permissions');
    return fetchRolePermissionsList();
  } catch (error) {
    throw new Error(getApiErrorMessage(error) || 'Failed to save role permissions');
  }
};

/**
 * Clears every menu permission for a role (revoke-all via empty detail list).
 */
export const clearRoleMenuPermissions = async (
  roleId: number
): Promise<ApiRolePermissions[]> => saveRoleMenuPermissions(roleId, []);
